import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight, GitCompare } from "lucide-react";
import { cloudStorage, subscribeCloudState } from "@/lib/cloudStore";

type SavedCareer = {
  id: string;
  title: string;
  family?: string;
  matchScore?: number;
};

const SAVED_KEY = "wekit_saved_careers";
const COMPARE_KEY = "wekit_compare_careers";

// Personal list: read from the learner's cloud record, never the device, so
// a career favourited on a phone is there on a laptop.
function read(key: string): SavedCareer[] {
  try {
    const raw = cloudStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export default function SidebarSavedCareers() {
  const [saved, setSaved] = useState<SavedCareer[]>([]);
  const [compareCount, setCompareCount] = useState(0);

  useEffect(() => {
    const sync = () => {
      setSaved(read(SAVED_KEY));
      setCompareCount(read(COMPARE_KEY).length);
    };
    sync();
    window.addEventListener("wekit:careers-updated", sync);
    const unsub = subscribeCloudState(sync);
    return () => {
      window.removeEventListener("wekit:careers-updated", sync);
      unsub();
    };
  }, []);

  if (saved.length === 0 && compareCount === 0) {
    return (
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3 px-2 flex items-center gap-1.5">
          <Star className="h-3 w-3 text-primary" /> Favourite Careers
        </p>
        <Link
          to="/report"
          className="block rounded-lg border border-dashed border-border/60 px-3 py-3 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
        >
          Save roles from your report to pin them here.
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 px-2 flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
          <Star className="h-3 w-3 text-primary" /> Favourite Careers
        </p>
        {saved.length > 0 && (
          <span className="text-[10px] font-semibold text-primary">{saved.length}</span>
        )}
      </div>

      <ul className="space-y-0.5">
        {saved.slice(0, 6).map((c) => (
          <li key={c.id}>
            <Link
              to={`/careers?q=${encodeURIComponent(c.title)}`}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              title={c.family ? `${c.title} · ${c.family}` : c.title}
            >
              <Star className="h-3.5 w-3.5 text-primary/70 shrink-0 fill-primary/20" />
              <span className="truncate flex-1">{c.title}</span>
              {typeof c.matchScore === "number" && (
                <span className="text-[10px] font-mono text-primary/80 shrink-0">
                  {c.matchScore}%
                </span>
              )}
              <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-60 shrink-0" />
            </Link>
          </li>
        ))}
      </ul>

      {saved.length > 6 && (
        <Link
          to="/report#careers"
          className="mt-2 block px-3 text-[11px] text-primary font-medium hover:underline"
        >
          View all {saved.length} →
        </Link>
      )}

      {compareCount > 0 && (
        <Link
          to="/report#careers"
          className="mt-3 flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-xs font-medium text-accent hover:bg-accent/10 transition-colors"
        >
          <GitCompare className="h-3.5 w-3.5" />
          <span>{compareCount} in compare</span>
          <ChevronRight className="h-3 w-3 ml-auto" />
        </Link>
      )}
    </div>
  );
}
